import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock, AlertCircle, Store, LinkIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { useForceLight, useInstallable } from "@/pages/mobile/MobileShell";
import { blueBtn } from "@/components/mobile/parts";
import { api, errMsg, imgUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

function Header({ info }) {
  return (
    <div className="bg-[#1565c0] px-6 pb-14 pt-12 text-center text-white">
      <span className="mx-auto mb-4 grid h-20 w-20 place-items-center overflow-hidden rounded-3xl bg-white shadow-lg shadow-blue-950/30">
        {info.logo_url ? <img src={imgUrl(info.logo_url)} alt={info.store} className="h-full w-full object-contain p-1.5" /> : <Store className="h-9 w-9 text-[#1565c0]" />}
      </span>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">App de comandas</p>
      <h1 className="mt-1 font-display text-3xl font-bold leading-tight" data-testid="m-link-store">{info.store}</h1>
    </div>
  );
}

function Invalid({ msg }) {
  return (
    <div className="grid min-h-[100dvh] place-items-center bg-slate-50 p-6 text-center" data-testid="m-link-invalid">
      <div className="space-y-3">
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-red-100"><LinkIcon className="h-7 w-7 text-red-600" /></span>
        <p className="font-display text-lg font-semibold">Link não encontrado</p>
        <p className="max-w-xs text-sm text-slate-500">{msg}</p>
        <Link to="/login" className="inline-block rounded-xl bg-[#1565c0] px-5 py-3 text-sm font-semibold text-white" data-testid="m-link-invalid-login">Entrar com o número de acesso</Link>
      </div>
    </div>
  );
}

export default function MLinkLogin() {
  const { token } = useParams();
  const { session, login } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [fail, setFail] = useState("");
  const [f, setF] = useState({ login: "", password: "", remember: true });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  useForceLight();
  useInstallable(info ? token : null);
  useEffect(() => {
    api.get(`/public/mobile-link/${token}`).then((r) => { setInfo(r.data); localStorage.setItem("m_link_token", token); }).catch((e) => setFail(errMsg(e)));
  }, [token]);

  if (fail) return <Invalid msg={fail} />;
  if (!info || session === null) return <div className="grid min-h-[100dvh] place-items-center bg-slate-50"><Loader2 className="h-7 w-7 animate-spin text-[#1565c0]" /></div>;
  if (session && session.tenant.access_number === info.access_number && !busy) return <Navigate to="/m" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLocked(false);
    setBusy(true);
    try {
      await login({ access_number: info.access_number, login: f.login.trim(), password: f.password, remember: f.remember });
      nav(localStorage.getItem("m_onboarding_done") ? "/m" : "/m/primeiro-acesso", { replace: true });
    } catch (err) {
      setError(errMsg(err));
      setLocked(err.response?.status === 429);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-slate-100 text-slate-900" data-testid="m-link-login">
      <div className="mx-auto min-h-[100dvh] max-w-md bg-slate-50">
        <Header info={info} />
        <form onSubmit={submit} className="relative -mt-8 mx-4 space-y-4 rounded-3xl bg-white p-6 shadow-xl shadow-slate-300/50 fade-up" data-testid="m-link-login-form">
          <div>
            <p className="font-display text-xl font-bold">Entrar</p>
            <p className="text-sm text-slate-500">Use o usuário e a senha que o responsável da loja cadastrou para você.</p>
          </div>
          {error && (
            <div className={cn("flex gap-2 rounded-xl p-3 text-sm", locked ? "bg-amber-100 text-amber-900" : "bg-red-100 text-red-700")} data-testid="m-link-login-error">
              {locked ? <Lock className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />}{error}
            </div>
          )}
          <label className="block space-y-1.5"><span className="text-sm font-semibold">Usuário</span>
            <Input value={f.login} onChange={(e) => setF((x) => ({ ...x, login: e.target.value }))} autoCapitalize="none" autoComplete="username" required className="h-12 bg-white text-base" data-testid="m-link-login-user" /></label>
          <label className="block space-y-1.5"><span className="text-sm font-semibold">Senha</span>
            <span className="relative block">
              <Input type={show ? "text" : "password"} value={f.password} onChange={(e) => setF((x) => ({ ...x, password: e.target.value }))} autoComplete="current-password" required className="h-12 bg-white pr-12 text-base" data-testid="m-link-login-password" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-0 grid w-12 place-items-center text-slate-400" aria-label={show ? "Esconder senha" : "Mostrar senha"} data-testid="m-link-login-toggle-password">{show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
            </span></label>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.remember} onCheckedChange={(v) => setF((x) => ({ ...x, remember: !!v }))} data-testid="m-link-login-remember" />Manter conectado neste celular</label>
          <Button type="submit" disabled={busy} className={cn("h-14 w-full rounded-xl text-lg", blueBtn)} data-testid="m-link-login-submit">{busy && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}Entrar</Button>
        </form>
        <p className="px-6 py-6 text-center text-xs text-slate-400">FSCsistemas · Chave de acesso {info.access_number}</p>
      </div>
    </div>
  );
}
